import { useState, useEffect } from 'react'
import LegalLayout from './LegalLayout'

const DEFAULT_PRIVACY_BODY =
  'At SalzburgCab, we respect your privacy and are committed to protecting your personal information. We collect and process your information in full compliance with the General Data Protection Regulation (GDPR) and Austrian data privacy laws.'

export default function PrivacyPolicy() {
  const [adminCustomText, setAdminCustomText] = useState<string | null>(null)

  useEffect(() => {
    try {
      const stored = localStorage.getItem('salzburg_dashboard_privacy_policy')
      if (stored && stored.trim().length > 0) {
        setAdminCustomText(stored.trim())
      }
    } catch {
      // fallback
    }
  }, [])

  return (
    <LegalLayout
      activeTab="privacy"
      title="Privacy Policy"
      subtitle="How Salzburg Cab collects, safeguards, and processes your personal data across our transport and booking services."
      lastUpdated="January 2026"
    >
      <div className="space-y-8 font-host text-gray-700 leading-relaxed text-sm sm:text-[15px]">
        {/* Intro */}
        <section className="border-b border-gray-100 pb-6">
          <p className="text-gray-900 font-medium text-base sm:text-lg">
            Welcome to Salzburg Cab. Your trust is our priority.
          </p>
          <p className="mt-3 text-gray-600">
            {adminCustomText || DEFAULT_PRIVACY_BODY}
          </p>
        </section>

        {/* Section 1: Data Controller */}
        <section className="space-y-3">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">
            1. Data Controller
          </h2>
          <p className="text-gray-600">
            The data controller responsible for the processing of your personal data is:
          </p>
          <div className="rounded-xl bg-gray-50 border border-gray-200/70 p-4 text-xs sm:text-sm text-gray-800 space-y-1">
            <p className="font-semibold text-gray-900">Salzburg Cab</p>
            <p>Rudolf Spängler Straße 10</p>
            <p>5020 Salzburg, Austria</p>
            <p>Email: <a href="mailto:office@salzburgcab.com" className="text-[#FF7027] hover:underline">office@salzburgcab.com</a></p>
            <p>Phone: <a href="tel:+43720775053" className="text-[#FF7027] hover:underline">+43 720 7750 53</a></p>
          </div>
        </section>

        {/* Section 2: Data We Collect */}
        <section className="space-y-3">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">
            2. Information We Collect
          </h2>
          <p className="text-gray-600">
            When you interact with our website, request a quote, or book private transfers, we may collect the following categories of personal data:
          </p>
          <ul className="list-disc pl-5 space-y-2 text-gray-600">
            <li>
              <strong className="text-gray-900">Identity & Contact Details:</strong> Full name, telephone number, email address, and billing address.
            </li>
            <li>
              <strong className="text-gray-900">Travel & Journey Data:</strong> Pickup and drop-off addresses, dates, departure times, flight numbers, train connections, stopovers, and special luggage requirements (e.g. ski bags, child booster seats).
            </li>
            <li>
              <strong className="text-gray-900">Payment Data:</strong> Payment method selection, transaction reference tokens, or cash receipt data (credit card numbers are processed securely by PCI-DSS certified payment gateways and are never stored on our servers).
            </li>
            <li>
              <strong className="text-gray-900">Device & Usage Information:</strong> IP address, browser type, operating system, and session timestamps for cybersecurity and analytics.
            </li>
          </ul>
        </section>

        {/* Section 3: Purposes of Processing */}
        <section className="space-y-3">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">
            3. Purpose & Legal Basis of Processing
          </h2>
          <p className="text-gray-600">
            We process your personal information under the following legal bases recognized by the GDPR:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
            <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-4">
              <h4 className="font-semibold text-gray-900">Contractual Performance (Art. 6(1)(b) GDPR)</h4>
              <p className="mt-1 text-xs text-gray-600">
                To reserve vehicles, assign licensed drivers, track flight arrivals, communicate itinerary confirmations, and deliver transport services.
              </p>
            </div>
            <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-4">
              <h4 className="font-semibold text-gray-900">Legal Obligation (Art. 6(1)(c) GDPR)</h4>
              <p className="mt-1 text-xs text-gray-600">
                To fulfill statutory bookkeeping, VAT invoicing, and Austrian commercial transport regulatory compliance.
              </p>
            </div>
            <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-4">
              <h4 className="font-semibold text-gray-900">Legitimate Interests (Art. 6(1)(f) GDPR)</h4>
              <p className="mt-1 text-xs text-gray-600">
                To improve fleet coordination, prevent fraud, ensure passenger and driver safety, and maintain service reliability.
              </p>
            </div>
            <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-4">
              <h4 className="font-semibold text-gray-900">Consent (Art. 6(1)(a) GDPR)</h4>
              <p className="mt-1 text-xs text-gray-600">
                For non-essential cookies or optional newsletter updates, which you may revoke at any time.
              </p>
            </div>
          </div>
        </section>

        {/* Section 4: Data Sharing */}
        <section className="space-y-3">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">
            4. Data Sharing & Third Parties
          </h2>
          <p className="text-gray-600">
            We do not sell, rent, or trade your personal data. We only share essential details with:
          </p>
          <ul className="list-disc pl-5 space-y-2 text-gray-600">
            <li>
              <strong className="text-gray-900">Assigned Professional Drivers:</strong> Drivers receive only your name, contact phone number, and pickup/destination instructions to execute the booked service.
            </li>
            <li>
              <strong className="text-gray-900">Payment & Invoicing Partners:</strong> Secure payment service providers for credit card settlement.
            </li>
            <li>
              <strong className="text-gray-900">Authorities:</strong> Law enforcement or regulatory authorities only when required by mandatory Austrian or EU legislation.
            </li>
          </ul>
        </section>

        {/* Section 5: Data Retention */}
        <section className="space-y-3">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">
            5. Data Retention
          </h2>
          <p className="text-gray-600">
            Personal data associated with completed journeys is retained only as long as necessary for the purpose for which it was collected, or as mandated by statutory retention periods (e.g. up to 7 years for financial and tax records under the Austrian Federal Fiscal Code). Operational journey dispatch logs are purged after 90 days.
          </p>
        </section>

        {/* Section 6: Your Rights */}
        <section className="space-y-3">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">
            6. Your GDPR Rights
          </h2>
          <p className="text-gray-600">
            As a data subject, you hold the following rights under EU privacy laws:
          </p>
          <ul className="list-disc pl-5 space-y-1.5 text-gray-600">
            <li>Right to access personal data stored about you (Art. 15 GDPR)</li>
            <li>Right to rectify inaccurate or incomplete records (Art. 16 GDPR)</li>
            <li>Right to erasure (&ldquo;Right to be forgotten&rdquo;) (Art. 17 GDPR)</li>
            <li>Right to restrict processing (Art. 18 GDPR)</li>
            <li>Right to data portability (Art. 20 GDPR)</li>
            <li>Right to object to processing based on legitimate interests (Art. 21 GDPR)</li>
          </ul>
          <p className="pt-2 text-gray-600">
            To exercise your rights, please submit a written request to{' '}
            <a href="mailto:office@salzburgcab.com" className="text-[#FF7027] font-medium hover:underline">
              office@salzburgcab.com
            </a>.
          </p>
        </section>
      </div>
    </LegalLayout>
  )
}
